import React, { useMemo } from 'react';
import { GoogleMap, Polygon, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '@/utils/googleMaps.jsx';

const DeliveryMap = ({ location, polygon, proposedPolygon }) => {
  const { isLoaded } = useGoogleMaps();

  // Convert polygon coordinates to Google Maps format
  const polygonPath = useMemo(() => {
    if (!polygon?.coordinates?.[0]) return [];
    
    return polygon.coordinates[0].map(coord => ({
      lat: coord[1], // latitude
      lng: coord[0]  // longitude
    }));
  }, [polygon]);

  // Convert proposed polygon coordinates to Google Maps format
  const proposedPolygonPath = useMemo(() => {
    if (!proposedPolygon?.coordinates?.[0]) return [];
    
    return proposedPolygon.coordinates[0].map(coord => ({
      lat: coord[1], // latitude
      lng: coord[0]  // longitude
    }));
  }, [proposedPolygon]);

  // Convert store location to Google Maps format
  const storeLocation = useMemo(() => {
    if (!location?.coordinates) return null;
    
    return {
      lat: location.coordinates[1], // latitude
      lng: location.coordinates[0]  // longitude
    };
  }, [location]);

  // Calculate map center and bounds
  const mapCenter = useMemo(() => {
    if (storeLocation) {
      return storeLocation;
    }
    
    // If no store location, center on polygons
    const allPaths = [...polygonPath, ...proposedPolygonPath];
    if (allPaths.length > 0) {
      // Calculate center manually to avoid dependency on window.google
      const latSum = allPaths.reduce((sum, point) => sum + point.lat, 0);
      const lngSum = allPaths.reduce((sum, point) => sum + point.lng, 0);
      return {
        lat: latSum / allPaths.length,
        lng: lngSum / allPaths.length
      };
    }
    
    return { lat: 34.0774671, lng: -118.3618112 }; // Default fallback
  }, [storeLocation, polygonPath, proposedPolygonPath]);

  // Calculate appropriate zoom level
  const mapZoom = useMemo(() => {
    const allPaths = [...polygonPath, ...proposedPolygonPath];
    if (allPaths.length === 0) return 12;
    
    // Calculate bounds manually to determine zoom
    const lats = allPaths.map(point => point.lat);
    const lngs = allPaths.map(point => point.lng);
    
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    
    // Determine zoom based on the larger dimension
    const maxDiff = Math.max(latDiff, lngDiff);
    
    if (maxDiff > 0.5) return 9;
    if (maxDiff > 0.2) return 10;
    if (maxDiff > 0.1) return 11;
    if (maxDiff > 0.05) return 12;
    if (maxDiff > 0.02) return 13;
    return 14;
  }, [polygonPath, proposedPolygonPath]);

  const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0'
  };

  const polygonOptions = {
    fillColor: '#3b82f6',
    fillOpacity: 0.2,
    strokeColor: '#1d4ed8',
    strokeOpacity: 0.8,
    strokeWeight: 2,
  };

  const proposedPolygonOptions = {
    fillColor: '#10b981',
    fillOpacity: 0.2,
    strokeColor: '#059669',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    strokeDashArray: [5, 5], // Dashed line to distinguish from current
  };

  const markerOptions = useMemo(() => {
    // Use a simple circle path as fallback
    const circlePath = 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z';
    
    return {
      icon: {
        path: circlePath,
        fillColor: '#ef4444',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.5,
      },
    };
  }, []);

  if (!isLoaded) {
    return (
      <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-gray-500">Loading map...</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={mapZoom}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          draggable: false,
          gestureHandling: 'none',
        }}
      >
        {/* Current Delivery Polygon */}
        {polygonPath.length > 0 && (
          <Polygon
            paths={polygonPath}
            options={polygonOptions}
          />
        )}
        
        {/* Proposed Delivery Polygon */}
        {proposedPolygonPath.length > 0 && (
          <Polygon
            paths={proposedPolygonPath}
            options={proposedPolygonOptions}
          />
        )}
        
        {/* Store Location Marker */}
        {storeLocation && (
          <Marker
            position={storeLocation}
            options={markerOptions}
            title="Store Location"
          />
        )}
      </GoogleMap>
      
      {/* Legend */}
      <div className="mt-3 flex items-center gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-500 rounded-full opacity-20"></div>
          <span>Current Delivery Area</span>
        </div>
        {proposedPolygonPath.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded-full opacity-20 border-2 border-green-500 border-dashed"></div>
            <span>Proposed New Delivery Area</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded-full"></div>
          <span>Store Location</span>
        </div>
      </div>
    </div>
  );
};

export default DeliveryMap;
